import type { ChangeEvent } from 'react';

export interface FieldProps {
  /** Ties the `<label>` to the `<input>`. A placeholder is not a label (convention skill § 9). */
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly type?: 'text' | 'email' | 'date';
  readonly inputMode?: 'text' | 'email' | 'numeric';
  readonly maxLength?: number;
  /**
   * The earliest value a `type="date"` control will offer, `YYYY-MM-DD`.
   *
   * Pass-through, and guidance rather than a guard: `min` greys out earlier days in the picker but a value
   * set programmatically — or typed — still arrives, so the screen's own validation is what refuses a past
   * date [decision 0172]. Both are wanted: the picker should not offer what the API will not take.
   */
  readonly min?: string;
  readonly placeholder?: string;
  /**
   * A hint under the control — the frame calls it out for the pay rate and the pre-filled last name.
   * Shown whether or not the field is in error; the error replaces nothing.
   */
  readonly hint?: string;
  /**
   * The validation message, shown only once the candidate has tried to continue.
   *
   * ⚠️ **Never colour alone** [`src/components/CLAUDE.md`]: the message is text, and the control also takes
   * `aria-invalid`, so a screen reader and a colourblind candidate both get the same fact a red border gives
   * everybody else.
   */
  readonly error?: string;
  /**
   * A fixed symbol drawn inside the control, before the value — the `$` on the pay rate [Operator,
   * 2026-09-07].
   *
   * **It is decoration, never data.** The candidate types digits; the symbol is not part of `value` and is
   * never sent. Rendering it inside the box rather than in the label is what makes the unit unambiguous at
   * the moment of typing — `20` and `$20` read differently, and the field previously showed a bare number
   * that Bullhorn stores as an hourly rate.
   *
   * `aria-hidden`, because the label and hint already say what the number means; a screen reader announcing
   * "dollar" between the label and the value is noise, not information.
   */
  readonly prefix?: string;
  /**
   * A fixed unit drawn inside the control, AFTER the value — the `/hr` on the pay rate.
   *
   * Same contract as `prefix`: decoration, never part of `value`, `aria-hidden` because the label and hint
   * already say what the number means. Right-aligned, so a long value runs under it rather than pushing it
   * off the control.
   */
  readonly suffix?: string;
  /**
   * Marks the field as required, which on this form means every field [0176].
   *
   * Draws the frame's red asterisk after the label AND sets `required` on the control, so the obligation
   * reaches a screen reader rather than being a colour a sighted user infers. The asterisk itself is
   * `aria-hidden` — `required` is the accessible signal, and announcing "asterisk" after every label is
   * noise.
   */
  readonly required?: boolean;
}

/**
 * A labelled text input [Figma · Input__text/Default, 3455:3662].
 *
 * Values read from the frame: label 16px semibold `--foreground`, 4px gap to the control, `--radius-xs`
 * (4px — the smallest radius in the system, not the 8px this originally used), a `--border-subtle` hairline,
 * and 8px padding with a 24px line-height.
 *
 * ⚠️ **40px tall, BELOW the convention's 44px touch floor (§ 9), by decision.** The frame draws a 40px
 * control inside a 64px field group, and an earlier build layered the floor on top — which made every field
 * 4px taller and, over five controls, put the form visibly out of step with the design. The Operator chose
 * the frame on 2026-08-13; the departure is recorded here and raised to design rather than absorbed.
 *
 * `autoComplete="off"` and the absent `name` are not cosmetic: this is a shared device, and browser autofill
 * offering the previous candidate's email would defeat the rule KID-1 exists to enforce.
 *
 * ⚠️ **This component's ERROR treatment is a known departure, and a frame now exists for it.**
 * `08 · Name already taken` [KAD-6d] draws a field in error as a **2px `--destructive`** edge with a danger
 * glyph and the message in `--subtle-foreground`; this renders 1px `--error-border` in `--error` with no
 * glyph, which predates any frame drawing the state. Reconciling it would restyle 18 call-sites across four
 * candidate screens whose own frames draw no error state to arbitrate it, so KAD-6d built
 * `features/enrolment/KioskNameField` to its frame instead and recorded the divergence, with its revisit
 * trigger, in `design-qa/known-departures.md`. ⛔ Do not "fix" one side of it in isolation.
 */
export function Field({
  id,
  label,
  value,
  onChange,
  type = 'text',
  inputMode = 'text',
  maxLength,
  min,
  placeholder,
  hint,
  error,
  prefix,
  suffix,
  required,
}: FieldProps) {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  const described = [
    ...(hint === undefined ? [] : [`${id}-hint`]),
    ...(error === undefined ? [] : [`${id}-error`]),
  ];

  return (
    <div className="flex w-full flex-col gap-1">
      {/*
        The asterisk is a SIBLING of the label, never a child of it. The accessible name of a control is
        the label's text content — `aria-hidden` does not remove a node from that computation — so an
        asterisk inside the element renames the field to "First name *". `required` on the control is what
        carries the obligation to a screen reader; this is the sighted candidate's copy of the same fact.
      */}
      <div className="flex items-center gap-1">
        <label className="text-base font-semibold leading-5 text-[var(--foreground)]" htmlFor={id}>
          {label}
        </label>
        {required === true && (
          <span aria-hidden="true" className="text-base leading-5 text-[var(--destructive)]">
            *
          </span>
        )}
      </div>
      <div className="relative w-full">
        {suffix !== undefined && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded-[var(--radius-xs)] px-2 text-sm leading-5 text-[var(--subtle-foreground)] ring-1 ring-[var(--border-subtle)]"
            data-affix="suffix"
          >
            {suffix}
          </span>
        )}
        {prefix !== undefined && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 rounded-[var(--radius-xs)] px-2 text-sm leading-5 text-[var(--subtle-foreground)] ring-1 ring-[var(--border-subtle)]"
            data-affix="prefix"
          >
            {prefix}
          </span>
        )}
        <input
          aria-describedby={described.length > 0 ? described.join(' ') : undefined}
          aria-invalid={error === undefined ? undefined : true}
          autoComplete="off"
          /*
          An inset ring rather than a border, for CONSISTENCY with `Button` — not because it changes a
          dimension here.

          The claim this comment used to make was wrong, and an independent review measured it: Tailwind's
          preflight sets `box-sizing: border-box`, and this control carries `h-10`, so its border was
          already drawn INSIDE the declared height. Old and new both measure 40.00px. The 42 never existed
          on this component; it is real on `Button`, which has no fixed height — 54 with a border, 52 with
          a ring.

          One difference the "identical" claim does not cover: a ring is a `box-shadow`, and box-shadows
          are dropped in forced-colors mode where a border survives. Low relevance on fixed kiosk hardware,
          written down rather than left implied.
        */
          /*
          The affix insets are the frame's own measurements, not padding chosen to look clear
          [`Input__text` in `Field/Desired Pay Rate`, I12021:40631;3455:3662, read live 2026-09-08].

          The frame lays the container out as: prefix chip at x=8, 25 wide, so its right edge is at 33;
          the value's text box starts at **x=41** — the chip plus an 8px gap. This carried `pl-5` (20px),
          which put the first digit 13px INSIDE the chip: the `$` printed on top of the `1` of `18.50`, at
          all three widths, on the first field a candidate fills. `pl-5` was the width of a bare `$` glyph
          and predates the chip's border and padding.

          The suffix is the same measurement from the other edge: the frame's chip sits 8px off the right
          edge and the value ends 8px before it. `/hr` renders 34px wide — the same as the frame's `kg`
          sample — so 8 + 34 + 8 lands on the frame's own 50.

          ⚠️ **Latent, and the reason this is a comment rather than a fix:** the inset is a constant, so
          it is right only while the suffix renders at or under 34px. `/month` or `/hora` would print
          under the value with nothing to catch it. There is exactly one suffix in the app today. Making
          it self-measuring means laying the affixes out as a flex row instead of absolutely positioning
          them, which changes the control's box for every field — its own change, not this one.
        */
          className={`kiosk-focusable h-10 w-full rounded-[var(--radius-xs)] bg-[var(--card)] p-2 text-base leading-6 text-[var(--foreground)] ring-1 ring-inset placeholder:text-[var(--subtle-foreground)] ${
            prefix === undefined ? '' : 'pl-[41px]'
          } ${suffix === undefined ? '' : 'pr-[50px]'} ${error === undefined ? 'ring-[var(--border-subtle)]' : 'ring-[var(--error-border)]'}`}
          id={id}
          inputMode={inputMode}
          maxLength={maxLength}
          min={min}
          onChange={handleChange}
          placeholder={placeholder}
          required={required}
          spellCheck={false}
          type={type}
          value={value}
        />
      </div>
      {hint !== undefined && (
        <span className="text-sm leading-5 text-[var(--subtle-foreground)]" id={`${id}-hint`}>
          {hint}
        </span>
      )}
      {error !== undefined && (
        <span className="text-sm leading-5 text-[var(--error)]" id={`${id}-error`} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
